import { Box, Tooltip, Typography } from "@mui/material";
import { operational } from "@mideye/design-tokens";
import type { ActivityPoint } from "@/types";
import { colors, eventTypeColors } from "@/theme/eventsTokens.ts";

const BAR_WIDTH = 4;
const BAR_GAP = 1;
const MAX_HEIGHT = 20;

const SEGMENTS = [
  { key: "blocked", label: "Blocked", color: eventTypeColors.blocked },
  { key: "failed_second_factor", label: "Failed Second Factor", color: eventTypeColors.failed_second_factor },
  { key: "invalid_password", label: "Invalid Password", color: eventTypeColors.invalid_password },
  { key: "user_not_found", label: "User Not Found", color: eventTypeColors.user_not_found },
  { key: "successful", label: "Successful", color: eventTypeColors.successful },
] as const;

function fmtDay(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function StackedBar(p: ActivityPoint, opts: { barW: number; maxH: number; max: number }) {
  const barH = Math.max(1, (p.total / opts.max) * opts.maxH);
  if (p.total === 0) return { rects: [], barH: 1 };
  let yOffset = opts.maxH;
  const rects = SEGMENTS.flatMap((seg) => {
    const val = p[seg.key];
    if (val <= 0) return [];
    const h = (val / p.total) * barH;
    yOffset -= h;
    return [{ key: seg.key, y: yOffset, w: opts.barW, h, fill: seg.color }];
  });
  return { rects, barH };
}

const TIP_BAR_H = 80;
const TIP_MAX_W = 420;

function TipBar({ p, max, compact }: { readonly p: ActivityPoint; readonly max: number; readonly compact: boolean }) {
  const barH = p.total > 0 ? Math.max(2, (p.total / max) * TIP_BAR_H) : 0;
  const barW = compact ? 8 : 16;
  const minW = compact ? 14 : 28;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px", flex: 1, minWidth: minW }}>
      <Box sx={{ width: barW, height: TIP_BAR_H, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
        {p.total === 0
          ? <Box sx={{ width: "100%", height: 1, bgcolor: colors.borderLight, borderRadius: 0.5 }} />
          : SEGMENTS.map((seg) => {
              const val = p[seg.key];
              if (val <= 0) return null;
              const h = Math.max(2, (val / p.total) * barH);
              return <Box key={seg.key} sx={{ width: "100%", height: h, bgcolor: seg.color, "&:first-of-type": { borderRadius: "2px 2px 0 0" }, "&:last-of-type": { borderRadius: "0 0 2px 2px" } }} />;
            })}
      </Box>
    </Box>
  );
}

function labelInterval(count: number): number {
  if (count <= 10) return 1;
  if (count <= 20) return 3;
  return 5;
}

function EnlargedChart({ points }: { readonly points: ActivityPoint[] }) {
  const max = Math.max(...points.map((p) => p.total), 1);
  const totals = SEGMENTS.map((s) => ({ ...s, value: points.reduce((sum, p) => sum + p[s.key], 0) })).filter((s) => s.value > 0);
  const compact = points.length > 10;
  const step = labelInterval(points.length);
  return (
    <Box sx={{ px: 2, pt: 1.5, pb: 1, maxWidth: TIP_MAX_W }}>
      <Box sx={{ overflowX: "auto", pb: 0.5, mb: 0.5 }}>
        <Box sx={{ display: "flex", gap: "1px", alignItems: "flex-end" }}>
          {points.map((p) => <TipBar key={p.date} p={p} max={max} compact={compact} />)}
        </Box>
        <Box sx={{ display: "flex", gap: "1px", mt: "3px" }}>
          {points.map((p, i) => {
            const minW = compact ? 14 : 28;
            const show = i === 0 || i === points.length - 1 || i % step === 0;
            return (
              <Box key={p.date} sx={{ flex: 1, minWidth: minW, textAlign: "center" }}>
                {show && <Typography sx={{ fontSize: 9, color: colors.textMuted, lineHeight: 1, whiteSpace: "nowrap" }}>{fmtDay(p.date)}</Typography>}
              </Box>
            );
          })}
        </Box>
      </Box>
      {totals.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, pt: 1, borderTop: `1px solid ${colors.borderLight}` }}>
          {totals.map((t) => (
            <Box key={t.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: t.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.4, flex: 1 }}>{t.label}</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: colors.textPrimary, lineHeight: 1.4, fontVariantNumeric: "tabular-nums" }}>{t.value.toLocaleString()}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function ActivitySparkline({ points }: { readonly points: ActivityPoint[] }) {
  if (points.length === 0) return null;
  const max = Math.max(...points.map((p) => p.total), 1);
  const width = points.length * (BAR_WIDTH + BAR_GAP) - BAR_GAP;

  return (
    <Tooltip
      title={<EnlargedChart points={points} />}
      placement="bottom-end"
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: operational.events.shadow.tooltip,
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
            maxWidth: "none",
            p: 0,
          },
        },
      }}
    >
      <Box
        component="svg"
        width={width}
        height={MAX_HEIGHT}
        sx={{ display: "block", flexShrink: 0, cursor: "pointer" }}
        aria-label="Daily activity"
      >
        {points.map((p, i) => {
          const x = i * (BAR_WIDTH + BAR_GAP);
          if (p.total === 0) {
            return <rect key={p.date} x={x} y={MAX_HEIGHT - 1} width={BAR_WIDTH} height={1} rx={1} fill={colors.borderLight} />;
          }
          const { rects } = StackedBar(p, { barW: BAR_WIDTH, maxH: MAX_HEIGHT, max });
          return (
            <g key={p.date}>
              {rects.map((r) => <rect key={r.key} x={x} y={r.y} width={r.w} height={r.h} fill={r.fill} />)}
            </g>
          );
        })}
      </Box>
    </Tooltip>
  );
}
